export function getErrorPayload(error) {
  return error?.payload || error?.response?.data || null;
}

export function getErrorStatus(error) {
  return error?.status || error?.response?.status || null;
}

export function describeRequestError(error, fallbackMessage = 'Request failed.') {
  const payload = getErrorPayload(error);
  const status = getErrorStatus(error);
  const message =
    payload?.userMessage ||
    payload?.message ||
    payload?.error ||
    error?.message ||
    fallbackMessage;

  if (!error?.response && !error?.payload && error?.request) {
    return {
      title: 'Network problem',
      message: 'Smarti could not reach the backend.',
      details: 'The browser sent the request, but no server response came back.',
      actions: [
        'Check that the Smarti backend is running.',
        'Verify your connection and try again.',
        'If you are using localhost, confirm the backend port is correct.',
      ],
      status,
    };
  }

  const title =
    payload?.title ||
    (status === 401 ? 'Session expired'
      : status === 403 ? 'Permission or security check failed'
        : status === 413 ? 'Upload too large'
          : status >= 500 ? 'Backend error'
            : 'Request could not be completed');

  const details =
    payload?.details ||
    payload?.detail ||
    payload?.technicalDetail ||
    payload?.code ||
    '';

  const actions =
    payload?.recoverySteps ||
    payload?.actions ||
    defaultActionsForStatus(status);

  return { title, message, details, actions, status };
}

export function describeSmartiLabRunError(error, isItalian = false) {
  const base = describeRequestError(
    error,
    isItalian ? 'Il run ML non è stato completato.' : 'The ML run did not complete.',
  );
  const raw = `${base.message} ${base.details || ''}`.toLowerCase();

  if (raw.includes('already running')) {
    return {
      ...base,
      title: isItalian ? 'Analisi già in corso' : 'Analysis already running',
      message: isItalian
        ? 'Smarti Lab sta già ricostruendo il modello ML.'
        : 'Smarti Lab is already rebuilding the ML model.',
      actions: isItalian
        ? ['Attendi il completamento del run attuale.', 'Controlla lo stato nella card del motore ML.']
        : ['Wait for the current run to finish.', 'Watch the ML engine status card for progress.'],
    };
  }

  if (raw.includes('python') || raw.includes('sklearn') || raw.includes('numpy') || raw.includes('scipy')) {
    return {
      ...base,
      title: isItalian ? 'Ambiente Python ML non pronto' : 'ML Python environment is not ready',
      message: isItalian
        ? 'Smarti Lab non riesce ad avviare lo stack Python richiesto dal modello.'
        : 'Smarti Lab cannot start the Python stack required by the model.',
      actions: isItalian
        ? ['Verifica che Python sia installato.', 'Installa le dipendenze ML dal file requirements.', 'Riprova il run dopo aver riavviato il backend.']
        : ['Verify Python is installed.', 'Install the ML dependencies from the requirements file.', 'Restart the backend, then run the analysis again.'],
    };
  }

  if (raw.includes('no such file') || raw.includes('not found') || raw.includes('database') || raw.includes('sqlite')) {
    return {
      ...base,
      title: isItalian ? 'Dati sorgente non disponibili' : 'Source data is not available',
      message: isItalian
        ? 'Smarti Lab non trova uno dei database o file necessari per costruire il modello.'
        : 'Smarti Lab cannot find one of the databases or files needed to build the model.',
      actions: isItalian
        ? ['Carica almeno un file produzione valido.', 'Carica dati burn-in se vuoi collegamenti Fusion.', 'Controlla che lo storage dati Smarti sia accessibile.']
        : ['Upload at least one valid production file.', 'Upload burn-in data if you want Fusion links.', 'Check that Smarti data storage is accessible.'],
    };
  }

  return {
    ...base,
    title: base.title || (isItalian ? 'Run ML non completato' : 'ML run did not complete'),
    actions: base.actions?.length ? base.actions : (isItalian
      ? ['Controlla che produzione e burn-in abbiano dati validi.', 'Riprova il run.', 'Se fallisce ancora, apri i log backend del run Fusion.']
      : ['Check that production and burn-in have valid data.', 'Run the analysis again.', 'If it still fails, inspect the Fusion backend run log.']),
  };
}

export function summarizeUploadResults(entries, kind = 'production') {
  const results = (entries || []).map((entry) => {
    const message = String(entry?.message || '').trim();
    const lowerMessage = message.toLowerCase();
    const explicitStatus = String(entry?.status || '').trim().toLowerCase();
    const duplicate = lowerMessage.includes('duplicate filename');
    const success = explicitStatus
      ? explicitStatus === 'imported'
      : kind === 'burn-in'
        ? Boolean(entry?.uploadId) && !duplicate
        : Boolean(entry?.global || entry?.data || entry?.sections) && !message;
    const skipped = explicitStatus
      ? ['duplicate', 'rejected', 'skipped'].includes(explicitStatus)
      : duplicate || lowerMessage.includes('skipped');
    return {
      filename: entry?.filename || entry?.global?.filename || 'Unknown file',
      status: success ? 'imported' : skipped ? 'skipped' : 'failed',
      message: success ? 'Imported successfully.' : message || 'The file could not be imported.',
      raw: entry,
    };
  });

  const imported = results.filter((item) => item.status === 'imported');
  const skipped = results.filter((item) => item.status === 'skipped');
  const failed = results.filter((item) => item.status === 'failed');

  return {
    results,
    importedCount: imported.length,
    skippedCount: skipped.length,
    failedCount: failed.length,
    success: failed.length === 0,
    title: failed.length
      ? `${failed.length} file${failed.length === 1 ? '' : 's'} could not be imported`
      : imported.length
        ? `${imported.length} file${imported.length === 1 ? '' : 's'} imported`
        : 'No new files were imported',
    message: buildUploadSummaryMessage(imported.length, skipped.length, failed.length, kind),
    actions: buildUploadActions(results, kind),
  };
}

function buildUploadSummaryMessage(importedCount, skippedCount, failedCount, kind) {
  if (failedCount > 0) {
    return kind === 'burn-in'
      ? 'Smarti reviewed the file, but at least one burn-in import failed. Check the file result below for the exact parser or format problem.'
      : 'Smarti reviewed the file, but at least one production import failed. Check the file result below for the exact metadata, format, or parser problem.';
  }
  if (importedCount > 0) {
    return `${importedCount} file${importedCount === 1 ? '' : 's'} imported successfully.${skippedCount ? ` ${skippedCount} duplicate or skipped file${skippedCount === 1 ? '' : 's'} ignored.` : ''}`;
  }
  if (skippedCount > 0) return 'All selected files were duplicates or skipped, so no new data was added.';
  return 'No file result came back from the server.';
}

function buildUploadActions(results, kind) {
  const messages = results.map((item) => item.message.toLowerCase()).join(' ');
  const actions = [];
  if (messages.includes('unsupported file type')) {
    actions.push(kind === 'burn-in'
      ? 'Use CSV, XLS, XLSX, XLT, or XLTX for burn-in uploads.'
      : 'Use CSV files for production uploads.');
  }
  if (messages.includes('burn-in file') || messages.includes('burn-in upload page')) {
    actions.push('Use the Burn-In upload page for this file.');
    actions.push('Use the Production upload page only for production test-result CSV files with Step_Number / Step_Result / Step_Name headers.');
  }
  if (messages.includes('supported header row') || messages.includes('header')) {
    actions.push('Open the file and confirm the expected header row is present.');
  }
  if (messages.includes('invalid serialnumber') || messages.includes('nouutsn')) {
    actions.push('Check that the production file contains a real serial number, not NOUUTSN.');
  }
  if (messages.includes('duplicate filename')) {
    actions.push('Rename the file or delete the existing import if you need to upload it again.');
  }
  if (!actions.length) {
    actions.push(kind === 'burn-in'
      ? 'Verify the workbook has burn-in rows with Sequence, Step Name, Status, Date, and Time columns.'
      : 'Verify the CSV has Smarti production metadata plus test step rows.');
  }
  return actions;
}

function defaultActionsForStatus(status) {
  if (status === 401) return ['Sign in again, then retry the action.'];
  if (status === 403) return ['Refresh the page and try again.', 'If you are not an admin, ask an administrator to run this action.'];
  if (status === 413) return ['Choose a smaller file.', 'Split the upload into smaller files.'];
  if (status >= 500) return ['Retry once.', 'If it fails again, check the backend logs for the exact exception.'];
  return [];
}
